/**
 * Enterprise Password Policy
 * 
 * Requirements:
 * - Minimum length: 12 characters
 * - Must include at least 1 uppercase letter
 * - Must include at least 1 lowercase letter
 * - Must include at least 1 number
 * - Symbols: optional (allowed but not required)
 * 
 * This must match the backend policy exactly.
 */

const MIN_LENGTH = 12;
const MAX_LENGTH = 128;

/**
 * Policy description for consistent messaging
 */
export const POLICY_DESCRIPTION = `Password must be at least ${MIN_LENGTH} characters long and include at least one uppercase letter, one lowercase letter, and one number.`;

/**
 * Human-readable requirements list
 */
export const POLICY_REQUIREMENTS = [
  `At least ${MIN_LENGTH} characters`,
  'At least one uppercase letter',
  'At least one lowercase letter',
  'At least one number',
  'Symbols are optional'
];

/**
 * Validates a password against the enterprise policy
 * @param {string} password - The password to validate
 * @returns {Object} { ok: boolean, errors: string[], errorMap: Object }
 *   errorMap contains specific error flags: { length, uppercase, lowercase, number }
 */
export function validatePassword(password) {
  const errors = [];
  const errorMap = {};

  if (!password || typeof password !== 'string') {
    return {
      ok: false,
      errors: ['Password is required'],
      errorMap: { required: true }
    };
  }

  // Check length
  if (password.length < MIN_LENGTH) {
    errors.push(`Password must be at least ${MIN_LENGTH} characters long`);
    errorMap.length = true;
  }

  if (password.length > MAX_LENGTH) {
    errors.push(`Password must be no more than ${MAX_LENGTH} characters long`);
    errorMap.length = true;
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
    errorMap.uppercase = true;
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
    errorMap.lowercase = true;
  }

  // Check for number
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
    errorMap.number = true;
  }

  // Symbols are optional, so we don't check for them

  return {
    ok: errors.length === 0,
    errors: errors,
    errorMap: errorMap
  };
}

/**
 * Gets a single error message combining all validation errors
 * @param {string} password - The password to validate
 * @returns {string|null} Combined error message or null if valid
 */
export function getPasswordErrorMessage(password) {
  const result = validatePassword(password);
  if (result.ok) {
    return null;
  }
  
  // Return a single combined message
  return result.errors.join('. ');
}

/**
 * Gets unmet requirements for a password (for real-time UI feedback)
 * @param {string} password - The password to check
 * @returns {Array<string>} Array of unmet requirement messages
 */
export function getUnmetRequirements(password) {
  if (!password) {
    return POLICY_REQUIREMENTS;
  }

  const result = validatePassword(password);
  const unmet = [];

  if (result.errorMap.length) {
    unmet.push(POLICY_REQUIREMENTS[0]); // At least 12 characters
  }
  if (result.errorMap.uppercase) {
    unmet.push(POLICY_REQUIREMENTS[1]); // At least one uppercase letter
  }
  if (result.errorMap.lowercase) {
    unmet.push(POLICY_REQUIREMENTS[2]); // At least one lowercase letter
  }
  if (result.errorMap.number) {
    unmet.push(POLICY_REQUIREMENTS[3]); // At least one number
  }

  return unmet;
}

